import React, { useState, useEffect } from 'react';
import { useSpring, animated } from '@react-spring/web';
import { cardPropTypes } from '../propTypes/cardPropTypes';
import { getCardRarityClass, getNameLength } from '../utils/cardUtils';
import { translatePitchRoll } from '../utils/gameUtils';
import useCardImage from '../hooks/useCardImage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGauge, faPersonRunning } from '@fortawesome/free-solid-svg-icons';
import PitcherFatigue, { getPitcherFatigueTier } from './PitcherFatigue';
import { parseCardUpgrades, getUpgradeLevel, getUpgradeOptionsForCardType, getEffectiveCardStatsFromUpgrades } from '../utils/cardUpgrades';
import { getLastTeamAbbrev } from '../helpers/teamHelpers';
import GLOBALS from '../config/globals';

// ── Sub-components ──────────────────────────────────────────────────────────

const CardLevelBadge = ({ level, upgradeLines }) => {
    const [show, setShow] = useState(false);
    return (
        <span
            className="card-level-tooltip-container"
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
        >
            <span className="card-level-badge">{level}</span>
            {show && (
                <span className="card-level-tooltip">
                    <strong>Upgrades</strong>
                    {upgradeLines.map((line, i) => (
                        <span key={i} className="card-level-tooltip-line">{line}</span>
                    ))}
                </span>
            )}
        </span>
    );
};

const BatterDice = ({ card, isHighlighted }) => (
    <div className="card-dice">
        <span className="b-dice">
            {[2,3,4,5,6,7,8,9,10,11,12].map(n => (
                <p key={n} className={isHighlighted(`d${n}`) ? 'highlight' : ''}>D{n}: {card[`d${n}`]}</p>
            ))}
        </span>
        <span className="x-dice">
            {[2,3,4,5,6,7,8,9,10,11,12].map(n => (
                <p key={n} className={isHighlighted(`xd${n}`) ? 'highlight' : ''}>XD{n}: {card[`xd${n}`]}</p>
            ))}
        </span>
    </div>
);

const PitcherDice = ({ card, isHighlighted }) => (
    <div className="card-dice">
        <span className="p-dice">
            {[2,3,4,5,6,7,8,9,10,11,12].map(n => (
                <p key={n} className={isHighlighted(`pd${n}`) ? 'highlight' : ''}>
                    PD{n}: {translatePitchRoll(card[`pd${n}`]).result}
                </p>
            ))}
        </span>
    </div>
);

// ── Main Card Component ─────────────────────────────────────────────────────

const Card = ({
    card = {},
    flip = false,
    flipKey = null,
    diceResultH = null,
    diceResultX = null,
    diceResultP = null,
    showNewCards = false,
    pitcherWork = null,
    variation = null,
    showLabel = false,
    hoverFlip = false,
    fallbackSeriesKey = null,
    frontOverlay = null,
}) => {
    // ── State ──
    const effectiveVariation = variation || card.CardInventory?.CardVariation || null;
    const [flipped, setFlipped] = useState(false);

    useEffect(() => {
        if (showNewCards) {
            setFlipped(false);
        } else {
            setFlipped(flip);
        }
    }, [flip, flipKey, showNewCards]);

    // ── Derived values (computed once, used by both sides) ──
    const seriesKey = card.Series?.[0]?.seriesKey ?? fallbackSeriesKey;
    const backgroundImage = useCardImage(card, seriesKey);
    const rarityClass = getCardRarityClass(card.rating);
    const nameLength = getNameLength(card.name);
    const teamAbbrev = getLastTeamAbbrev(card.teamNameID);
    const isBatter = card.type === 'Batter';
    const upgrades = parseCardUpgrades(card.CardInventory?.upgrades);
    const { effectiveBatterSpeed, effectivePitcherStamina } =
        getEffectiveCardStatsFromUpgrades({ card, upgrades, globals: GLOBALS });
    const displaySpeed = effectiveBatterSpeed ?? card.speed;
    const displayStamina = effectivePitcherStamina ?? card.stamina;

    // ── Animation ──
    const { transform, opacity } = useSpring({
        opacity: flipped ? 1 : 0,
        transform: `rotateY(${flipped ? 180 : 0}deg)`,
        config: { mass: 5, tension: 500, friction: 80 },
    });

    const isHighlighted = rowName => (
        (diceResultH && rowName === `d${diceResultH}`) ||
        (diceResultX && rowName === `xd${diceResultX}`) ||
        (diceResultP && rowName === `pd${diceResultP}`)
    );

    // ── Shared header (both sides) ──
    const cardHeader = (
        <div className="card-header">
            <span className={`square ${rarityClass}`}>{card.rating}</span>
            {!card.customImage && teamAbbrev && (
                <span className="card-team-abbrev">{teamAbbrev}</span>
            )}
            <span className={`card-name card-name-length-${nameLength}`}>
                {card.customImage ? '' : card.name}
            </span>
        </div>
    );

    // ── Shared footer (front only — back has no footer) ──
    const cardFooter = (
        <div className="card-footer">
            {!isBatter && pitcherWork !== null && (
                <span className="tooltip-container">
                    <PitcherFatigue
                        pitcherWork={pitcherWork}
                        stamina={displayStamina}
                        showLabel
                    />
                    <span className="tooltip">
                        {getPitcherFatigueTier(pitcherWork, displayStamina)}: {pitcherWork}/{card.stamina}
                        {displayStamina !== card.stamina ? ` (+${displayStamina - card.stamina})` : ''}
                    </span>
                </span>
            )}
            <span className="card-footer-items">
                <span className="tooltip-container">
                    {isBatter ? (
                        <FontAwesomeIcon
                            icon={faPersonRunning}
                            className={`speed-icon-${Math.round(displaySpeed / 10) * 10}`}
                        />
                    ) : (
                        <FontAwesomeIcon
                            icon={faGauge}
                            className={`stamina-icon-${Math.min(27, Math.round(displayStamina / 3) * 3)}`}
                        />
                    )}
                    <span className="tooltip">
                        {isBatter
                            ? `Speed: ${card.speed}${displaySpeed !== card.speed ? ` (+${displaySpeed - card.speed})` : ''}`
                            : `Stamina: ${card.stamina}${displayStamina !== card.stamina ? ` (+${displayStamina - card.stamina})` : ''}`}
                    </span>
                </span>
                <span className="card-positions">{card.positions}</span>
                {(() => {
                    const options = getUpgradeOptionsForCardType(isBatter);
                    const level = getUpgradeLevel(upgrades, options);
                    if (level <= 0) return null;
                    const lines = options
                        .map(opt => (upgrades[opt.key] ?? 0) > 0 ? `${opt.label}: ${upgrades[opt.key]}` : null)
                        .filter(Boolean);
                    return <CardLevelBadge level={level} upgradeLines={lines} />;
                })()}
            </span>
        </div>
    );

    // ── Render ──
    return (
        <div className={showLabel ? 'card-with-label' : undefined}>
            <div
                className={`card-container ${effectiveVariation?.cssClass || ''}`}
                onClick={hoverFlip ? undefined : () => setFlipped(!flipped)}
                onMouseEnter={hoverFlip ? () => setFlipped(true) : undefined}
                onMouseLeave={hoverFlip ? () => setFlipped(false) : undefined}
            >
                <animated.div className="c card-front" style={{ opacity: opacity.to(o => 1 - o), transform }}>
                    <div
                        className="card-face card-face--front"
                        style={{
                            backgroundImage,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                        }}
                    />
                    <div className="card-face card-content-overlay">
                        {cardHeader}
                        <div className="card-body" />
                        {cardFooter}
                    </div>
                    {frontOverlay}
                </animated.div>
                <animated.div
                    className="c card-back"
                    style={{
                        opacity,
                        transform: transform.to(t => `${t} rotateY(180deg)`),
                    }}
                >
                    <div className="card-face card-face--back">
                        {cardHeader}
                        <div className="card-body">
                            {isBatter
                                ? <BatterDice card={card} isHighlighted={isHighlighted} />
                                : <PitcherDice card={card} isHighlighted={isHighlighted} />}
                        </div>
                    </div>
                </animated.div>
            </div>
            {showLabel && effectiveVariation && (
                <span className="card-label variation-label">{effectiveVariation.name}</span>
            )}
        </div>
    );
};

Card.propTypes = cardPropTypes;

export default Card;
